import React from 'react';
import {UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {nameRegExp, TextFieldForm, Typography} from 'web-library';
import {KeyValueResponse} from '../../../../api';
import {
  UserCitizenShipsResponse
} from '../../../../api-refactored/non-national-exam-registration/useGetUserCitizenShips';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';

interface IParticularParentDetail {
  title: string;
  classes: any;
  labelName: string;
  labelCitizen: string;
  labelDocType: string;
  labelDocNo: string;
  labelWorkPassType: string;
  labelFin: string;

  nameErr?: boolean;
  nameErrMsg?: string;
  citizenErr?: boolean;
  citizenErrMsg?: string;
  docTypeErr?: boolean;
  docTypeErrMsg?: string;
  docNoErr?: boolean;
  docNoErrMsg?: string;
  workPassTypeErr?: boolean;
  workPassTypeErrMsg?: string;
  finErr?: boolean;
  finErrMsg?: string;

  dataCitizen: UserCitizenShipsResponse[] | undefined;
  dataDocType: any;
  dataWorkPassType: any;

  formHook: UseFormReturn<any>;

  hasEditAccess: boolean;
  isDisableDocType: boolean;
  isDisableDocNo: boolean;
  isDisableWpt: boolean;
  isDisableFin: boolean;

  isHiddenDocType: boolean;
  isHiddenDocNo: boolean;
}

const NA = 'NOT APPLICABLE';

const ParticularParentDetail = ({
                                  title,
                                  classes,
                                  labelName,
                                  nameErr,
                                  nameErrMsg,
                                  dataCitizen,
                                  citizenErr,
                                  citizenErrMsg,
                                  labelCitizen,
                                  dataDocType,
                                  docTypeErr,
                                  docTypeErrMsg,
                                  labelDocType,
                                  dataWorkPassType,
                                  labelWorkPassType,
                                  finErr,
                                  finErrMsg,
                                  workPassTypeErr,
                                  workPassTypeErrMsg,
                                  labelFin,
                                  labelDocNo,
                                  docNoErr,
                                  docNoErrMsg,
                                  formHook,
                                  hasEditAccess,
                                  isDisableDocType,
                                  isDisableDocNo,
                                  isDisableWpt,
                                  isDisableFin,
                                  isHiddenDocType,
                                  isHiddenDocNo
                                }: IParticularParentDetail) => {
  const {t} = useTranslation();
  const {control, watch} = formHook;

  const citizenWatch = watch(labelCitizen);
  const watchWorkPassType = watch(labelWorkPassType);
  return (
      <>
        <Typography variant="h3" className={classes.sectionTitle}>
          {title}
        </Typography>
        <TextFieldForm
            id={labelName === 'fatherName' ? 'input-fatherName' : 'input-motherName'}
            className={classes.inputSection}
            label={t('exam->>ParentParticulars.Name')}
            isRequired
            form={formHook}
            name={labelName}
            type="text"
            error={nameErr}
            helperText={nameErrMsg}
            placeholder="Enter name"
            disabled={!hasEditAccess}
            regExpToBlock={nameRegExp}
        />
        <ControlledAutocomplete
            id={
              labelCitizen === 'fatherCitizen'
                  ? 'inputSelect-fatherCitizen'
                  : 'inputSelect-motherCitizen'
            }
            isRequired
            name={labelCitizen}
            control={control}
            label={t('exam->>ParentParticulars.CitizenShip')}
            options={dataCitizen || []}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
            isOptionEqualToValue={(option: KeyValueResponse, value: KeyValueResponse) =>
                option.code === value.code
            }
            defaultValue={undefined}
            className={classes.inputSection}
            placeholder="Select citizenship"
            error={citizenErr}
            helperText={citizenErrMsg}
            disableClearable
            readOnly
            disabled={!hasEditAccess}
        />
        <ControlledAutocomplete
            id={
              labelDocType === 'fatherDocType'
                  ? 'inputSelect-fatherDocType'
                  : 'inputSelect-motherDocType'
            }
            isRequired
            hidden={isHiddenDocType}
            name={labelDocType}
            error={docTypeErr}
            helperText={docTypeErrMsg}
            control={control}
            label={t('exam->>ParentParticulars.DocType')}
            options={dataDocType}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
            isOptionEqualToValue={(option: any, value: any) => option.code === value.code}
            getOptionDisabled={(option) => option?.disabled}
            defaultValue={null}
            disabled={isDisableDocType || !hasEditAccess}
            className={classes.inputSection}
            placeholder="Select document type"
            disableClearable
            readOnly
        />

        <TextFieldForm
            id={labelDocNo === 'fatherDocNo' ? 'input-fatherDocNo' : 'input-motherDocNo'}
            className={classes.inputSection}
            label={t('exam->>ParentParticulars.DocNo')}
            isRequired
            style={{display: isHiddenDocNo ? 'none' : 'block'}}
            form={formHook}
            name={labelDocNo}
            type="text"
            error={docNoErr}
            helperText={docNoErrMsg}
            placeholder="Enter document no."
            disabled={isDisableDocNo}
        />
        <ControlledAutocomplete
            id={
              labelWorkPassType === 'fatherWorkPassType'
                  ? 'inputSelect-fatherWorkPassType'
                  : 'inputSelect-motherWorkPassType'
            }
            isRequired
            name={labelWorkPassType}
            control={control}
            label={t('exam->>ParentParticulars.WorkPassType')}
            options={dataWorkPassType || []}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
            isOptionEqualToValue={(option: any, value: any) => option.code === value.code}
            className={classes.inputSection}
            placeholder="Select work pass type"
            defaultValue={null}
            error={workPassTypeErr}
            helperText={workPassTypeErrMsg}
            disabled={isDisableWpt || !hasEditAccess}
            disableClearable
            readOnly
        />

        <TextFieldForm
            id={labelFin === 'fatherFin' ? 'input-fatherFin' : 'input-motherFin'}
            className={classes.inputField}
            label={t('exam->>ParentParticulars.FIN')}
            isRequired
            form={formHook}
            name={labelFin}
            onChange={(e) => formHook.setValue(`${labelFin}`, e.target.value.toUpperCase())}
            type="text"
            error={finErr && watchWorkPassType?.description !== NA}
            helperText={
              finErr && (watchWorkPassType?.description !== NA || citizenWatch?.code === 'OTHERS')
                  ? finErrMsg
                  : ''
            }
            defaultValue=""
            disabled={isDisableFin || !hasEditAccess}
            placeholder="Enter foreign identification no."
        />
      </>
  );
};

export default ParticularParentDetail;
